/**
 * The app's `UniqueId` of each level, room, wall, opening and item (owner's decision of 13 October 2026): kept as the
 * file has it, fresh for every object made in the web, a copy too, never derived; given at the store's commits and when
 * the web makes a project.
 */
import type { Item, Level, Opening, Room, Scene, Wall } from './types';

/** The text of .NET's `Guid.NewGuid().ToString()`, as `newUniqueId` makes it. */
export const UNIQUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const newUniqueId = (): string => crypto.randomUUID();

type Placed = 'walls' | 'openings' | 'items';
const PLACED: readonly Placed[] = ['walls', 'openings', 'items'];

/** A level's ids by what names an object across an edit: its IR id, a room's number. */
interface Identities { walls: Map<string, string | undefined>; openings: Map<string, string | undefined>; items: Map<string, string | undefined>; rooms: Map<number, string | undefined> }
const identities = new WeakMap<Level, Identities>();

function identitiesOf(level: Level): Identities {
  let known = identities.get(level);
  if (!known) {
    const ids = (list: ReadonlyArray<Wall | Opening | Item>) => new Map(list.map(object => [object.id, object.uniqueId]));
    known = { walls: ids(level.walls), openings: ids(level.openings), items: ids(level.items), rooms: new Map(level.rooms.map(room => [room.index, room.uniqueId])) };
    identities.set(level, known);
  }
  return known;
}

function withUniqueId<T extends { uniqueId?: string }>(object: T, uniqueId: string | undefined): T {
  if (object.uniqueId === uniqueId) return object;
  if (uniqueId !== undefined) return { ...object, uniqueId };
  const { uniqueId: _dropped, ...rest } = object;
  return rest as T;
}

function mapped<T>(list: T[], next: (value: T) => T): T[] {
  let out: T[] | undefined;
  list.forEach((value, index) => {
    const changed = next(value);
    if (changed !== value) (out ??= list.slice())[index] = changed;
  });
  return out ?? list;
}

function pairLevels(before: Scene, next: Scene): Map<Level, Level> {
  const pairs = new Map<Level, Level>(), taken = new Set<Level>(), old = new Set(before.levels);
  const pair = (level: Level, source: Level) => { pairs.set(level, source); taken.add(source); };
  for (const level of next.levels) if (old.has(level) && !taken.has(level)) pair(level, level);
  const shared: Array<[number, Level, Level]> = [];
  for (const level of next.levels) {
    if (pairs.has(level)) continue;
    for (const source of before.levels) {
      if (taken.has(source)) continue;
      const known = identitiesOf(source);
      let count = 0;
      for (const kind of PLACED) for (const object of level[kind]) if (known[kind].has(object.id)) count++;
      if (count) shared.push([count, level, source]);
    }
  }
  shared.sort((a, b) => b[0] - a[0]);
  for (const [, level, source] of shared) if (!pairs.has(level) && !taken.has(source)) pair(level, source);
  for (const level of next.levels) {
    const source = pairs.has(level) ? undefined : before.levels.find(value => !taken.has(value) && value.walls === level.walls);
    if (source) pair(level, source);
  }
  // An agent's draft is a deep copy, so an empty floor is known there by its own id alone.
  for (const level of next.levels) {
    if (pairs.has(level) || !level.uniqueId) continue;
    const holders = next.levels.filter(value => !pairs.has(value) && value.uniqueId === level.uniqueId);
    const sources = before.levels.filter(value => !taken.has(value) && value.uniqueId === level.uniqueId);
    if (holders.length === 1 && sources.length === 1) pair(level, sources[0]);
  }
  if (before.levels.length === next.levels.length) {
    next.levels.forEach((level, index) => { if (!pairs.has(level) && !taken.has(before.levels[index])) pair(level, before.levels[index]); });
  }
  return pairs;
}

/**
 * `next` with each object's `UniqueId` decided against `before`: an object `before` has keeps its id there, or none; any
 * other gets a fresh one in place of what a copy brought along. Untouched objects keep their reference, `next` itself too.
 */
export function assignUniqueIds(before: Scene, next: Scene): Scene {
  if (before === next) return next;
  const pairs = pairLevels(before, next);
  const elsewhere = (kind: Placed, id: string): string | undefined => {
    for (const level of before.levels) {
      const known = identitiesOf(level)[kind];
      if (known.has(id)) return known.get(id);
    }
    return newUniqueId();
  };
  let changed = false;
  const levels = next.levels.map(level => {
    const source = pairs.get(level);
    if (source === level) return level;
    const known = source && identitiesOf(source);
    const placed = <T extends Wall | Opening | Item>(kind: Placed) => (object: T) =>
      withUniqueId(object, known?.[kind].has(object.id) ? known[kind].get(object.id) : elsewhere(kind, object.id));
    const rooms = mapped(level.rooms, (room: Room) => withUniqueId(room, known?.rooms.has(room.index) ? known.rooms.get(room.index) : newUniqueId()));
    const walls = mapped(level.walls, placed<Wall>('walls')), openings = mapped(level.openings, placed<Opening>('openings')), items = mapped(level.items, placed<Item>('items'));
    const uniqueId = source ? source.uniqueId : newUniqueId();
    if (uniqueId === level.uniqueId && rooms === level.rooms && walls === level.walls && openings === level.openings && items === level.items) return level;
    changed = true;
    return withUniqueId({ ...level, rooms, walls, openings, items }, uniqueId);
  });
  return changed ? { ...next, levels } : next;
}

/** A project the web makes: every level, room and object is new, so each gets a fresh id (owner's decision of 13 October 2026). */
export const freshUniqueIds = (scene: Scene): Scene => assignUniqueIds({ ...scene, levels: [] }, scene);
